import { provideZonelessChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideServiceWorker } from '@angular/service-worker';

import { provideOptimus } from '@openng/optimus-ui/config';
import Aura from '@openng/optimus-ui-themes/aura';
import { definePreset } from '@openng/optimus-ui-themes';
import { MessageService } from '@openng/optimus-ui/api';

import { environment } from 'src/environments/environment';

import { menubarHeaderDesignToken } from './app/theme/menubar-header.design-token';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { TRATON_BLUE, TRATON_WARM_GREY } from './app/theme/design-tokens';

const tratonPreset = definePreset(Aura, {
	semantic: {
		primary: {
			50: TRATON_BLUE[50],
			100: TRATON_BLUE[100],
			200: TRATON_BLUE[200],
			300: TRATON_BLUE[300],
			400: TRATON_BLUE[400],
			500: TRATON_BLUE[500],
			600: TRATON_BLUE[600],
			700: TRATON_BLUE[700],
			800: TRATON_BLUE[800],
			900: TRATON_BLUE[850],
			950: TRATON_BLUE[950],
		},

		colorScheme: {
			light: {
				primary: {
					color: TRATON_BLUE[800],
					contrastColor: '#FFFFFF',
					hoverColor: TRATON_BLUE[700],
					activeColor: TRATON_BLUE[600],
				},
				content: {
					background: TRATON_WARM_GREY[100], // Card/panel backgrounds
				},
				surface: {
					0: TRATON_WARM_GREY[100], // Card bg, text color in dark mode
					50: TRATON_WARM_GREY[150], // Table header, filled form field bg
					100: TRATON_WARM_GREY[200], // Table row hover, list hover
					200: TRATON_WARM_GREY[200], // Borders, content border
					300: TRATON_WARM_GREY[200], // Form field border
					400: TRATON_WARM_GREY[300], // Icons, form hover border
					500: TRATON_WARM_GREY[400], // Muted/placeholder text
					600: TRATON_WARM_GREY[500], // --
					700: TRATON_WARM_GREY[600], // Primary text color
					800: TRATON_WARM_GREY[700], // Text hover color
					900: TRATON_WARM_GREY[800], // Dark bg (dark mode content)
					950: TRATON_WARM_GREY[950], // Darkest (dark mode form bg)
				},
			},
			dark: {
				primary: {
					color: TRATON_BLUE[400],
					contrastColor: TRATON_BLUE[950],
					hoverColor: TRATON_BLUE[300],
					activeColor: TRATON_BLUE[200],
				},
				content: {
					background: TRATON_BLUE[950], // Card/panel backgrounds
				},
				surface: {
					0: TRATON_BLUE[50], // Text color (lightest)
					50: TRATON_BLUE[850], // Table header, filled form field bg
					100: TRATON_BLUE[800], // Table row hover, list hover
					200: TRATON_BLUE[850], // Borders, content border
					300: TRATON_BLUE[300], // Form field border
					400: TRATON_BLUE[400], // Icons, muted text
					500: TRATON_BLUE[500], // Placeholder text
					600: TRATON_BLUE[600], // --
					700: TRATON_BLUE[850], // Content border
					800: TRATON_BLUE[850], // Content hover bg
					900: TRATON_BLUE[900], // Content bg (cards)
					950: TRATON_BLUE[900], // Form field bg
				},
			},
		},
	},
	components: {
		menubar: menubarHeaderDesignToken,
	},
});

// Merge Optimus UI config into appConfig
appConfig.providers = [
	...appConfig.providers,
	MessageService,
	provideOptimus({
		theme: {
			options: {
				darkModeSelector: '.theme-dark',
			},
			preset: tratonPreset,
		},
		// Start every datepicker's week on Monday (PrimeNG default is 0 = Sunday). Applied globally
		// here so all calendars app-wide are consistent without per-instance [firstDayOfWeek].
		translation: {
			firstDayOfWeek: 1,
		},
	}),
];

bootstrapApplication(AppComponent, {
	...appConfig,
	providers: [
		provideZonelessChangeDetection(),
		...appConfig.providers,
		provideServiceWorker('ngsw-worker.js', {
			enabled: environment.serviceWorkerEnabled,
			// We primarily use service worker to check for updates, so register ASAP.
			// We might want to change this to "registerWhenStable" if use cases change.
			registrationStrategy: 'registerImmediately',
		}),
	],
}).catch(err => console.error(err));
